import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ease } from "@/components/motion/Reveal";
import { StepVehicle, StepService, StepProblem, StepDate, StepContact, StepSummary } from "@/components/booking/steps";
import { submitBooking } from "@/components/booking/submit";
import { bookingServices, company } from "@/data/site";

const steps = [
  { key: "vehicle", label: "Fahrzeug", title: "Welches Fahrzeug bringen Sie?" },
  { key: "service", label: "Service", title: "Worum geht es?" },
  { key: "problem", label: "Anliegen", title: "Was können wir für Sie tun?" },
  { key: "date", label: "Termin", title: "Wann passt es Ihnen?" },
  { key: "contact", label: "Kontakt", title: "Wie erreichen wir Sie?" },
  { key: "summary", label: "Prüfen", title: "Alles korrekt?" },
];

const validate = (i, d) => {
  const e = {};
  if (i === 0) {
    if (!d.brand.trim()) e.brand = "Bitte Marke angeben.";
    if (!d.model.trim()) e.model = "Bitte Modell angeben.";
  }
  if (i === 1 && !d.service) e.service = "Bitte einen Service wählen.";
  if (i === 3 && !d.date) e.date = "Bitte einen Wunschtermin wählen.";
  if (i === 4) {
    if (d.name.trim().length < 2) e.name = "Bitte Namen angeben.";
    if (d.phone.trim().length < 5) e.phone = "Bitte Telefonnummer angeben.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.email)) e.email = "Bitte gültige E-Mail-Adresse angeben.";
    if (!d.privacy) e.privacy = "Bitte Einverständnis bestätigen.";
  }
  return e;
};

export const BookingWizard = () => {
  const [params] = useSearchParams();
  const preService = params.get("service");
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [d, setD] = useState({
    brand: "", model: "", year: "", mileage: "", plate: "",
    service: bookingServices.includes(preService) ? preService : "",
    message: params.get("note") ? `Hinweis: ${params.get("note")}\n` : "",
    date: "", time: "", name: "", phone: "", email: "", privacy: false,
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => {
    setD((s) => ({ ...s, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };
  const go = (n) => { setDir(n > i ? 1 : -1); setI(n); };
  const next = () => {
    const e = validate(i, d);
    if (Object.keys(e).length) { setErrors(e); return; }
    go(i + 1);
  };
  const send = async () => {
    setBusy(true);
    try {
      const r = await submitBooking(d, files);
      setResult(r);
      toast.success("Terminanfrage gesendet.");
    } catch (err) {
      toast.error("Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder rufen Sie uns an.");
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }} className="max-w-2xl" data-testid="booking-success">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[color:var(--plum)] text-white"><Check className="h-7 w-7" strokeWidth={2} /></span>
        <h2 className="h-section mt-10">Danke, {d.name.split(" ")[0]}.</h2>
        <p className="lead mt-6">Ihre Terminanfrage ist bei uns eingegangen. Wir prüfen den Wunschtermin und melden uns persönlich. Eine Anfrage ist noch keine Buchungsbestätigung.</p>
        <p className="mt-6 mono text-[13px] text-[color:var(--ink-3)]">Referenz: <span className="text-[color:var(--ink)]" data-testid="booking-reference">{result.id.slice(0, 8).toUpperCase()}</span></p>
        {!result.configured && <p className="mt-3 text-[13px] text-[color:var(--ink-3)]" data-testid="booking-email-notice">Hinweis (Entwicklung): E-Mail-Versand via Web3Forms ist noch nicht konfiguriert. Die Anfrage wurde gespeichert.</p>}
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/" data-testid="booking-back-home" className="btn-dark">Zur Startseite</Link>
          <a href={company.phoneHref} className="btn-outline">{company.phone}</a>
        </div>
      </motion.div>
    );
  }

  const Step = [StepVehicle, StepService, StepProblem, StepDate, StepContact, StepSummary][i];

  return (
    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16" data-testid="booking-wizard">
      <ol className="lg:col-span-3 flex lg:flex-col gap-x-5 gap-y-4 overflow-x-auto pb-2 lg:pb-0 -mx-5 px-5 lg:mx-0 lg:px-0" aria-label="Schritte">
        {steps.map((s, n) => (
          <li key={s.key} className="shrink-0">
            <button type="button" disabled={n > i} onClick={() => go(n)} data-testid={`booking-step-${n}`} aria-current={n === i ? "step" : undefined}
              className={cn("flex items-center gap-3 text-[14px] transition-colors", n === i ? "text-[color:var(--ink)]" : n < i ? "text-[color:var(--ink-2)] hover:text-[color:var(--ink)]" : "text-[color:var(--ink-3)] cursor-default")}>
              <span className={cn("mono text-[11px] h-7 w-7 rounded-full border flex items-center justify-center transition-[background-color,border-color]", n === i ? "bg-[color:var(--plum)] text-white border-[color:var(--plum)]" : n < i ? "bg-[color:var(--violet-soft)] text-[color:var(--violet)] border-transparent" : "hairline")}>
                {n < i ? <Check className="h-3 w-3" strokeWidth={3} /> : String(n + 1).padStart(2, "0")}
              </span>
              {s.label}
            </button>
          </li>
        ))}
      </ol>

      <div className="lg:col-span-9">
        <div className="h-px bg-[color:var(--line)] mb-10 relative overflow-hidden">
          <motion.span className="absolute inset-y-0 left-0 bg-[color:var(--violet)]" animate={{ width: `${((i + 1) / steps.length) * 100}%` }} transition={{ duration: 0.7, ease }} />
        </div>
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div key={i} custom={dir} initial={{ opacity: 0, x: dir * 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: dir * -24 }} transition={{ duration: 0.5, ease }}>
            <p className="eyebrow mb-4">Schritt {i + 1} von {steps.length}</p>
            <h2 className="h-sub mb-10" data-testid="booking-step-title">{steps[i].title}</h2>
            <Step d={d} set={set} errors={errors} files={files} setFiles={setFiles} goTo={go} />
          </motion.div>
        </AnimatePresence>

        <div className="mt-14 flex items-center justify-between gap-4 border-t hairline pt-8">
          <button type="button" onClick={() => go(i - 1)} disabled={i === 0} data-testid="booking-back-button" className="btn-outline disabled:opacity-0 disabled:pointer-events-none">
            <ArrowLeft className="h-4 w-4" /> Zurück
          </button>
          {i < steps.length - 1 ? (
            <button type="button" onClick={next} data-testid="booking-next-button" className="btn-dark">Weiter <ArrowRight className="h-4 w-4" /></button>
          ) : (
            <button type="button" onClick={send} disabled={busy} data-testid="booking-submit-button" className="btn-violet disabled:opacity-60">
              {busy ? "Wird gesendet…" : "Terminanfrage senden"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
